import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core'
import { ActivatedRoute, Params, Router } from '@angular/router'
import { Meta, Title } from '@angular/platform-browser'
import { BehaviorSubject, Subscription } from 'rxjs'
import { take } from 'rxjs/operators'
import { SwiperOptions } from 'swiper/types/swiper-options'

import { SEOService } from '@flowaccount/landing'
import { TranslateService } from '@ngx-translate/core'
import { TransferStateService, isScullyGenerated } from '@scullyio/ng-lib'
import { SquidexService } from '../../../services/squidex.service'

interface ConnectionData {
  data: {
    nameUrl: {
      iv: string
    }
  }
}

interface ConnectionInformation {
  connectionName: string
}

@Component({
  selector: 'flowaccount-platform-businesses-connect',
  templateUrl: './platform-businesses-connect.component.html',
  styleUrl: './platform-businesses-connect.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class PlatformBusinessesConnectComponent implements OnInit, OnDestroy {
  public ConnectionList: any
  public config: SwiperOptions
  private readonly CONNECTION_SERVICE_KEY = 'connection-service-key'
  private ConnectionData: BehaviorSubject<ConnectionData[]> = new BehaviorSubject<ConnectionData[]>([])
  private connectionNameUrl: string
  private ConnectionService: Subscription
  private connectionInformation: ConnectionInformation = { connectionName: '' }
  private langChangeSubscription: Subscription

  constructor(
    public translate: TranslateService,
    private activatedRoute: ActivatedRoute,
    protected tstate: TransferStateService,
    private seoService: SEOService,
    private titleService: Title,
    public readonly squidexService: SquidexService,
    public cdref: ChangeDetectorRef,
    private meta: Meta,
    private router: Router,
  ) {
    this.activatedRoute.params.subscribe((params: Params) => {
      this.connectionNameUrl = params['nameUrl']
    })
    this.langChangeSubscription = translate.onLangChange.subscribe((res) => {
      const setContent = res.lang === 'th' ? 'index' : 'noindex'
      this.meta.updateTag({ name: 'robots', content: setContent })
      this.setMeta()
    })
  }
  ngOnInit(): void {
    // After scully builded
    if (isScullyGenerated()) {
      this.tstate
        .useScullyTransferState(this.CONNECTION_SERVICE_KEY, this.ConnectionList)
        .subscribe((data: ConnectionData[]) => {
          this.ConnectionData.next(data)
          this.filterConnectionNameUrl()
          this.cdref.markForCheck()
        })
    } else {
      this.getConnectionService()
    }
    this.swiperConfig()
  }

  ngOnDestroy(): void {
    this.langChangeSubscription?.unsubscribe()
    this.ConnectionService?.unsubscribe()
  }

  private setMeta(): void {
    this.connectionInformation = {
      connectionName:
        this.connectionNameUrl.trim().charAt(0).toUpperCase() + this.connectionNameUrl.replace(/-/g, ' ').slice(1),
    }

    const metaTitle = this.translate.instant('metatitle-Connection-Information', this.connectionInformation)
    const metaDescription = this.translate.instant('metaDescription-Connection-Information', this.connectionInformation)

    this.titleService.setTitle(metaTitle)
    this.seoService.createCanonicalURL(metaDescription)
    this.seoService.createOg(
      'https://flowaccountcdn.com/new_landing/image/platform-businesses/become-our-partner/our-integrations_og_image.jpg',
      metaTitle,
      metaDescription,
    )
  }

  private swiperConfig(): void {
    this.config = {
      observer: true,
      observeParents: true,
      mousewheel: {
        forceToAxis: true,
      },
      speed: 700,
      slidesPerView: 'auto',
      injectStyles: [
        ` .swiper-pagination  {
            bottom: 0 !important;
          }
          .swiper-pagination-bullet {
            width: 10px;
            height: 10px;
            border-radius: 50%;
            background-color: white;
          }
          .swiper-pagination-bullet-active {
            background-color: white;
          }
        `,
      ],
      pagination: {
        clickable: true,
      },
    }
  }

  private async getConnectionService() {
    this.ConnectionService = await this.squidexService
      .getContentSquidexApiOrder('platform-businesses')
      .pipe(take(1))
      .subscribe((data: any[]) => {
        this.ConnectionData.next(data)
        this.filterConnectionNameUrl()
        this.setMeta()
        this.tstate.setState(this.CONNECTION_SERVICE_KEY, data)
        this.cdref.markForCheck()
      })
  }

  private filterConnectionNameUrl(): void {
    this.ConnectionList =
      this.ConnectionData?.value?.filter((item) => item?.data.nameUrl.iv === this.connectionNameUrl) ?? []
    if (
      this.ConnectionList.length === 0 &&
      this.connectionNameUrl &&
      (this.router.url.startsWith('/our-integrations/') || this.router.url.startsWith('/en/our-integrations/'))
    ) {
      window.location.href = '/notfound'
    }
    this.cdref.markForCheck()
  }
}
